import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import { apiError } from "@/lib/errors";
import { HELP_SECTIONS, HELP_ROLE_OPTIONS } from "@/lib/help";

const BLANK = { title: "", section: "All Users", roles: HELP_ROLE_OPTIONS.map(r => r[0]), content: "", faqs: [] };

export default function HelpTopicEditor({ topic, onClose, onSaved }) {
  const [form, setForm] = useState(topic ? { ...BLANK, ...topic, faqs: topic.faqs || [] } : BLANK);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setFaq = (i, k, v) => set("faqs", form.faqs.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  const toggleRole = (r, on) => set("roles", on ? [...form.roles, r] : form.roles.filter(x => x !== r));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    const body = { title: form.title, section: form.section, roles: form.roles, content: form.content,
      faqs: form.faqs.filter(f => f.q.trim()) };
    try {
      const { data } = topic ? await api.put(`/help/topics/${topic.id}`, body) : await api.post("/help/topics", body);
      toast.success("Guide topic saved");
      onSaved(data);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none border-2 border-[#0A1128] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="help-editor-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{topic ? "EDIT TOPIC" : "NEW TOPIC"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid md:grid-cols-[1fr_220px] gap-3">
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={e => set("title", e.target.value)} className="rounded-sm" data-testid="help-editor-title"/>
            </div>
            <div>
              <Label>Section</Label>
              <Select value={form.section} onValueChange={v => set("section", v)}>
                <SelectTrigger className="rounded-sm" data-testid="help-editor-section"><SelectValue/></SelectTrigger>
                <SelectContent>{HELP_SECTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Visible to</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
              {HELP_ROLE_OPTIONS.map(([r, label]) => (
                <label key={r} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={form.roles.includes(r)} onCheckedChange={v => toggleRole(r, !!v)} data-testid={`help-editor-role-${r}`}/>{label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label>Content</Label>
            <p className="text-[11px] text-slate-500 mb-1">Use headings, numbered steps, links and the image button to insert screenshots by URL.</p>
            <RichTextEditor rich value={form.content} onChange={v => set("content", v)} testid="help-editor-content" placeholder="Write the guide…"/>
          </div>
          <div className="space-y-3" data-testid="help-editor-faqs">
            <div className="flex items-center justify-between">
              <Label>FAQs</Label>
              <Button type="button" size="sm" variant="outline" className="rounded-sm h-8" onClick={() => set("faqs", [...form.faqs, { q: "", a: "" }])} data-testid="help-editor-add-faq">
                <Plus className="w-3 h-3 mr-1"/>Add FAQ
              </Button>
            </div>
            {form.faqs.map((f, i) => (
              <div key={i} className="border border-slate-200 p-3 space-y-2 bg-slate-50" data-testid={`help-editor-faq-${i}`}>
                <div className="flex gap-2">
                  <Input placeholder="Question" value={f.q} onChange={e => setFaq(i, "q", e.target.value)} className="rounded-sm bg-white" data-testid={`help-editor-faq-q-${i}`}/>
                  <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={() => set("faqs", form.faqs.filter((_, j) => j !== i))} data-testid={`help-editor-faq-remove-${i}`}>
                    <Trash2 className="w-4 h-4"/>
                  </Button>
                </div>
                <RichTextEditor rich value={f.a} onChange={v => setFaq(i, "a", v)} testid={`help-editor-faq-a-${i}`} placeholder="Answer…"/>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-[#0A1128] hover:bg-slate-800" data-testid="help-editor-save">
            {saving ? "Saving..." : "Save Topic"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
